import { useEffect } from 'react';
import { useAutoRefillStore } from '../../stores/useAutoRefillStore';
import { getSessionUser } from '../../utils/session';
import { AutoRefillHeader } from './components/AutoRefillHeader';
import { AutoRefillTable } from './components/AutoRefillTable';
import { AutoRefillTerms } from './components/AutoRefillTerms';

export interface AutoRefillMountProps {
  userId: string | null;
  config: {
    customerServicePhone: string;
    customerServiceEmail: string;
  };
  navigate: {
    toRefills: (linkToken?: string) => void;
  };
}

export function AutoRefillPage({ userId, config, navigate }: AutoRefillMountProps) {
  const {
    refillUsers,
    selectedLinkToken,
    prescriptions,
    loading,
    noAutoFills,
    error,
    loadUsers,
    setSelectedUser,
    fetchAutoRefills,
  } = useAutoRefillStore();

  // Initialise user list and derive starting link token on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional run-once on mount
  useEffect(() => {
    const sessionUser = getSessionUser();
    if (sessionUser) {
      loadUsers(sessionUser);
    }
  }, []);

  // Once refillUsers is populated, resolve the initial link token
  // biome-ignore lint/correctness/useExhaustiveDependencies: run only when user list first populates
  useEffect(() => {
    if (refillUsers.length === 0) {
      return;
    }
    const initialToken = userId ?? refillUsers[0]?.LinkToken ?? null;
    if (initialToken) {
      setSelectedUser(initialToken);
      void fetchAutoRefills(initialToken);
    }
  }, [refillUsers]);

  const displayToken = selectedLinkToken ?? refillUsers[0]?.LinkToken ?? '';

  return (
    <div>
      {/* Page header */}
      <div className="panel formulary-header">
        <div className="col-lg-12 col-md-12 col-sm-12 mobile-activate-auto-header">
          <h1>
            Automatic Refill <small>Manage your automatic medication refill below.</small>
          </h1>
          <div className="link-to-refills">
            <button
              type="button"
              className="btn btn-default"
              style={{ padding: 0, borderColor: 'transparent', outline: 'none' }}
              onClick={() => navigate.toRefills(displayToken)}
            >
              Back To Refills
            </button>
          </div>
          <hr />
        </div>
      </div>

      {/* User selector + view toggle */}
      <AutoRefillHeader config={config} />

      {/* Terms copy */}
      <div className="text-info auto-refill-warning">
        <p>
          By requesting automatic refills below, you can ensure your{' '}
          {window.mxAppConfig?.ClientName ?? 'pharmacy'} prescriptions are received without
          interruption. Simply select the medications you would like to have refilled automatically
          from your prescription history. For your safety, the automatic refill program will make
          sure you have just the right amount on hand. You should have a 2-3 week supply remaining
          from the previous fill when the program will begin processing your next order. Please note
          certain medications, such as controlled substances or prescriptions written for less than
          a 30 day supply, are not eligible for automatic refills.
        </p>
        <p id="auto-refill-warning-bullet">
          Auto-refill processing dates will not match the date on your prescription bottle or the
          Next Refill date under My Refills on the member portal due to the safety features
          associated with the auto-refill program.
        </p>
      </div>

      {/* Main panel */}
      <div className="col-md-12 mobile-autorefill-content">
        <div className="panel">
          {/* Prescription list */}
          {loading && (
            <div className="spinner">
              <div className="rect1" />
              {' '}
              <div className="rect2" />
              {' '}
              <div className="rect3" />
              {' '}
              <div className="rect4" />
              {' '}
              <div className="rect5" />
              <h4>Loading Prescriptions</h4>
            </div>
          )}

          {!loading && error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {!noAutoFills && <AutoRefillTable prescriptions={prescriptions} />}
              <AutoRefillTerms config={config} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
